import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as BankIcon,
  Upload as UploadIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Receipt as TemplateIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  SettingsBackupRestore as RestoreConfigIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from '@mui/x-data-grid';
import { styled, useTheme } from '@mui/material/styles';
import BankFormModal from './BankFormModal';
import BankImportModal from './BankImportModal';
import TemplateFormModal from './TemplateFormModal';
import CopyTemplateDialog from './CopyTemplateDialog';
import BankConfigRestoreDialog from './BankConfigRestoreDialog';
import TemplateHistoryDialog from './TemplateHistoryDialog';
import { useBankService } from '../../../services/banks/bankService';
import { bankBackupService } from '../../../services/banks/bankBackupService';
import { companyPreferencesService } from '../../../services/settings/companyPreferencesService';
import { chequeTemplateService } from '../../../services/templates/chequeTemplateService';
import { useSnackbar } from '../../../hooks/useSnackbar';
import SnackbarNotification from '../../common/Feedback/SnackbarNotification';
import { EasyCheckButton, EasyCheckDataGridBasic } from '../../common/Controls';
import { themeUtils } from '../../../theme';

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.2, 1, 0.2, 1),
  height: 'calc(100vh - 100px)',
  maxHeight: 'calc(100vh - 100px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: theme.palette.background.default,
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1.5), // Further reduced margin
  padding: theme.spacing(3, 0), // Same as beneficiary page (24px top and bottom)
  flexShrink: 0, // Prevent shrinking
}));

// Using centralized EasyCheckDataGrid instead of custom styled component

const ActionButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 0,
  margin: theme.spacing(0, 0.25),
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const CurrencyChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 24,
  '& .MuiChip-label': {
    padding: theme.spacing(0, 1),
  },
}));

const BankSetupPage = () => {
  const theme = useTheme();
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const { snackbar, showSnackbar, hideSnackbar, showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedBankDetail, setSelectedBankDetail] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedBankForTemplate, setSelectedBankForTemplate] = useState(null);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceBank, setCopySourceBank] = useState(null);
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyBank, setHistoryBank] = useState(null);

  const bankService = useBankService();

  // Bank Configuration Backup module (preference-gated)
  useEffect(() => {
    companyPreferencesService.getPreferences().then((result) => {
      if (result.success) setBackupEnabled(result.data.enableBankBackup === true);
    });
  }, []);

  const handleExportConfig = async () => {
    try {
      setExporting(true);
      const configDocument = await bankBackupService.exportConfig();
      const fileName = bankBackupService.downloadConfigFile(configDocument);
      showSuccess(`Bank configuration exported to ${fileName}`);
    } catch (error) {
      showError(error.message || 'Failed to export bank configuration');
    } finally {
      setExporting(false);
    }
  };

  const handleOpenHistory = (bank) => {
    setHistoryBank(bank);
    setHistoryDialogOpen(true);
  };

  // Snackbar functionality is now handled by useSnackbar hook

  const reloadBanks = useCallback(async () => {
    try {
      setLoading(true);
      
      // Always load ALL records to enable client-side filtering
      console.log('📄 Loading ALL records for client-side filtering');
      const result = await bankService.getAllBanks(1, 10000);
      
      console.log('📄 API result:', result);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        
        const banksData = Array.isArray(data) ? data.map(bank => ({
          id: bank.Id || bank.id,
          accountNumber: bank.AccountNumber || bank.accountNumber,
          accountName: bank.AccountName || bank.accountName,
          currency: bank.Currency || bank.currency,
          companyId: bank.CompanyId || bank.companyId,
          isActive: bank.IsActive !== undefined ? bank.IsActive : bank.isActive,
          isDefault: bank.IsDefault !== undefined ? bank.IsDefault : bank.isDefault,
          hasChequeTemplate: bank.HasChequeTemplate !== undefined ? bank.HasChequeTemplate : bank.hasChequeTemplate || false,
          createdDate: bank.CreatedDate || bank.createdDate,
          modifiedDate: bank.ModifiedDate || bank.modifiedDate,
          createdUser: bank.CreatedUser || bank.createdUser,
          modifiedUser: bank.ModifiedUser || bank.modifiedUser
        })) : [];
        
        setBanks(banksData);
        setRowCount(banksData.length);
        console.log('📄 Loaded', banksData.length, 'records for client-side filtering');

        // Template statuses are now available directly from the API response
        // No need to make separate API calls
      } else {
        setBanks([]);
        setRowCount(0);
        showError('Failed to load banks');
      }
    } catch (error) {
      console.error('Error loading banks:', error);
      setBanks([]);
      setRowCount(0);
      showError('Error loading banks');
    } finally {
      setLoading(false);
    }
  }, []); // Remove bankService dependency to prevent re-renders

  // Load banks only once on mount
  useEffect(() => {
    reloadBanks();
  }, []); // Empty dependency array

  // Manual reload function for explicit calls (delete, save, import)
  const manualReloadBanks = useCallback(async () => {
    try {
      setLoading(true);
      
      // Always load ALL records to enable client-side filtering
      console.log('📄 Manual reload - Loading ALL records for client-side filtering');
      const result = await bankService.getAllBanks(1, 10000);
      
      console.log('📄 Manual reload API result:', result);
      
      if (result.success || result.Success) {
        const data = result.data || result.Data || [];
        
        const banksData = Array.isArray(data) ? data.map(bank => ({
          id: bank.Id || bank.id,
          accountNumber: bank.AccountNumber || bank.accountNumber,
          accountName: bank.AccountName || bank.accountName,
          currency: bank.Currency || bank.currency,
          companyId: bank.CompanyId || bank.companyId,
          isActive: bank.IsActive !== undefined ? bank.IsActive : bank.isActive,
          isDefault: bank.IsDefault !== undefined ? bank.IsDefault : bank.isDefault,
          hasChequeTemplate: bank.HasChequeTemplate !== undefined ? bank.HasChequeTemplate : bank.hasChequeTemplate || false,
          createdDate: bank.CreatedDate || bank.createdDate,
          modifiedDate: bank.ModifiedDate || bank.modifiedDate,
          createdUser: bank.CreatedUser || bank.createdUser,
          modifiedUser: bank.ModifiedUser || bank.modifiedUser
        })) : [];
        
        // Only update state if data actually changed
        setBanks(prevBanks => {
          if (JSON.stringify(prevBanks) !== JSON.stringify(banksData)) {
            console.log('📄 Data changed, updating banks');
            return banksData;
          }
          console.log('📄 Data unchanged, skipping update');
          return prevBanks;
        });
        setRowCount(banksData.length);
        console.log('📄 Manual reload - Loaded', banksData.length, 'records for client-side filtering');

        // Template statuses are now available directly from the API response
        // No need to make separate API calls
      } else {
        setBanks([]);
        setRowCount(0);
        showError('Failed to load banks');
      }
    } catch (error) {
      console.error('Error in manual reload banks:', error);
      setBanks([]);
      setRowCount(0);
              showError('Error loading banks');
    } finally {
      setLoading(false);
    }
  }, [bankService]);

  const handleAddBank = () => {
    setSelectedBank(null);
    setModalOpen(true);
  };

  const handleEditBank = (bank) => {
    setSelectedBank(bank);
    setModalOpen(true);
  };

  const handleDeleteBank = async (bankId) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      try {
        const result = await bankService.deleteBank(bankId);
        if (result.success) {
          showSuccess('Bank account deleted successfully');
          await manualReloadBanks();
        } else {
          showError('Failed to delete bank account');
        }
      } catch (error) {
        console.error('Error deleting bank:', error);
        showError('Error deleting bank account');
      }
    }
  };

  const handleModalClose = () => {
    console.log('🔧 handleModalClose called');
    setModalOpen(false);
    setSelectedBank(null);
    console.log('🔧 Modal state set to closed');
  };

  const handleBankSave = async (bankData) => {
    console.log('💾 Starting bank save operation...', { selectedBank: !!selectedBank, bankData });
    try {
      let result;
      if (selectedBank) {
        console.log('💾 Updating existing bank...', selectedBank.id);
        result = await bankService.updateBank(selectedBank.id, bankData);
      } else {
        console.log('💾 Creating new bank...');
        result = await bankService.createBank(bankData);
      }

      console.log('💾 Save result:', result);

      if (
        result.success ||
        result.Success ||
        result.status === 201 ||
        (typeof result.message === 'string' && result.message.toLowerCase().includes('created'))
      ) {
        showSuccess(
          selectedBank 
            ? 'Bank account updated successfully' 
            : 'Bank account created successfully'
        );
        console.log('💾 Save successful, closing modal and reloading...');
        
        // Close modal first
        setModalOpen(false);
        setSelectedBank(null);
        
        // Reset pagination if creating new bank
        if (!selectedBank) {
          setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
        }
        
        // Reload banks after modal is closed
        setTimeout(() => {
          manualReloadBanks();
        }, 100);
        
        return result;
      } else {
        console.log('💾 Save failed:', result.message || result.Message);
        showError(result.message || result.Message || 'Failed to save bank account');
        // Don't throw error, just return to keep modal open for user to fix
        return;
      }
    } catch (error) {
      console.error('💾 Error saving bank:', error);
      showError('Error saving bank account');
      // Don't throw error, just return to keep modal open
      return;
    }
  };

  const handleImportOpen = () => {
    setImportModalOpen(true);
  };

  const handleImportClose = () => {
    setImportModalOpen(false);
  };

  const handleImportComplete = async (results) => {
    if (results.summary.successful > 0) {
      showSuccess(`Import completed: ${results.summary.successful} successful, ${results.summary.failed} failed`);
      await manualReloadBanks();
    } else {
      showWarning('Import completed with errors. Please check the results.');
    }
  };

  const getCurrencyColor = (currency, theme) => {
    const currencyColorMap = {
      AED: theme.palette.primary.main,
      OMR: themeUtils.colors.blue['blue-500'],
      SAR: themeUtils.colors.blue['blue-600'], 
      BHD: themeUtils.colors.blue['blue-400'],
      KWD: theme.palette.primary.dark,
      QAR: themeUtils.colors.blue['blue-300'],
      EGP: themeUtils.colors.blue['blue-700'],
    };
    return currencyColorMap[currency] || theme.palette.primary.main;
  };

  const columns = [
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      flex: 1,
      minWidth: 150,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'accountName',
      headerName: 'Account Name',
      flex: 1.5,
      minWidth: 200,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'currency',
      headerName: 'Currency',
      width: 120,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: ['AED', 'OMR', 'SAR', 'BHD', 'KWD', 'QAR', 'EGP', 'EUR'],
      renderCell: (params) => {
        const currencyColor = getCurrencyColor(params.value, theme);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <CurrencyChip
              label={params.value}
              size="small"
              sx={{
                backgroundColor: `${currencyColor}15`,
                color: currencyColor,
                border: `1px solid ${currencyColor}30`,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'templateStatus',
      headerName: 'Template Status',
      width: 130,
      maxWidth: 140,
      filterable: true,
      sortable: true,
      type: 'singleSelect',
      valueOptions: ['Configured', 'Not Configured'],
      renderCell: (params) => {
        const configured = isTemplateConfigured(params.row);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Chip
              icon={configured ? <CheckIcon /> : <CancelIcon />}
              label={configured ? 'Configured' : 'Not Configured'}
              size="small"
              sx={{
                backgroundColor: configured ? '#D1FAE5' : '#FEF3C7',
                color: configured ? '#059669' : '#d97706',
                border: `1px solid ${configured ? '#10b981' : '#f59e0b'}`,
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: configured ? '#059669' : '#d97706',
                }
              }}
            />
          </Box>
        );
      },
      valueGetter: (value, row) => {
        return isTemplateConfigured(row) ? 'Configured' : 'Not Configured';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: backupEnabled ? 220 : 180,
      maxWidth: backupEnabled ? 240 : 200,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
          <Stack direction="row" spacing={0.5}>
            <ActionButton
              onClick={() => handleConfigureTemplate(params.row)}
              sx={{
                color: isTemplateConfigured(params.row) ? theme.palette.success.main : theme.palette.warning.main,
                backgroundColor: isTemplateConfigured(params.row) ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 152, 0, 0.1)'
              }}
              size="small"
              title={isTemplateConfigured(params.row) ? "Edit Template" : "Configure Template"}
            >
              <TemplateIcon fontSize="small" />
            </ActionButton>
            {isTemplateConfigured(params.row) && (
              <ActionButton
                onClick={() => handleOpenCopyDialog(params.row)}
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: 'rgba(37, 99, 235, 0.1)',
                }}
                size="small"
                title="Copy Template to Other Accounts"
              >
                <CopyIcon fontSize="small" />
              </ActionButton>
            )}
            {backupEnabled && isTemplateConfigured(params.row) && (
              <ActionButton
                onClick={() => handleOpenHistory(params.row)}
                sx={{
                  color: theme.palette.info.main,
                  backgroundColor: 'rgba(2, 136, 209, 0.1)',
                }}
                size="small"
                title="Template Version History"
              >
                <HistoryIcon fontSize="small" />
              </ActionButton>
            )}
            <ActionButton
              onClick={() => handleEditBank(params.row)}
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.primary.main,
                }
              }}
              size="small"
              title="Edit Bank"
            >
              <EditIcon fontSize="small" />
            </ActionButton>
            <ActionButton
              onClick={() => handleDeleteBank(params.row.id)}
              sx={{ 
                color: '#E57373',
                '&:hover': {
                  color: theme.palette.error.main,
                }
              }}
              size="small"
              title="Delete Bank"
            >
              <DeleteIcon fontSize="small" />
            </ActionButton>
          </Stack>
        </Box>
      ),
    },
  ];

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer sx={{ borderBottom: '1px solid #f5f5f5', py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridToolbarColumnsButton
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              '&:hover': {
                borderColor: 'primary.main',
              },
            }}
          />
          <GridToolbarExport
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              '&:hover': {
                borderColor: 'primary.main',
              },
            }}
          />
        </Box>
      </GridToolbarContainer>
    );
  };

  const rows = useMemo(() => {
    const allRows = Array.isArray(banks) ? banks.map((bank, index) => ({
      id: bank.id || index,
      accountNumber: bank.accountNumber,
      accountName: bank.accountName,
      currency: bank.currency,
      hasChequeTemplate: bank.hasChequeTemplate,
      ...bank
    })) : [];

    if (!searchQuery.trim()) return allRows;

    const query = searchQuery.toLowerCase();
    return allRows.filter(row =>
      (row.accountNumber && row.accountNumber.toLowerCase().includes(query)) ||
      (row.accountName && row.accountName.toLowerCase().includes(query)) ||
      (row.currency && row.currency.toLowerCase().includes(query))
    );
  }, [banks, searchQuery]);

  const handleViewDetails = (bank) => {
    setSelectedBankDetail(bank);
    setDetailModalOpen(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalOpen(false);
    setSelectedBankDetail(null);
  };

  const handleConfigureTemplate = async (bank) => {
    try {
      console.log('🔧 Loading template for bank:', bank);
      // Get existing template for this bank
      const existingTemplate = await chequeTemplateService.getTemplateByBankId(bank.id);
      console.log('🔧 Retrieved template data:', existingTemplate);
      setSelectedBankForTemplate(bank);
      setCurrentTemplate(existingTemplate);
      setTemplateModalOpen(true);
    } catch (error) {
      console.error('Error loading template:', error);
      showError('Error loading template configuration');
    }
  };

  const handleTemplateModalClose = () => {
    setTemplateModalOpen(false);
    setSelectedBankForTemplate(null);
    setCurrentTemplate(null);
  };

  const handleTemplateSave = async (templateData) => {
    try {
      console.log('💾 Saving template:', templateData);
      await chequeTemplateService.saveTemplate(templateData);
      showSuccess('Template saved successfully!');
      handleTemplateModalClose();
      
      // Reload banks to get updated HasChequeTemplate status with a slight delay
      setTimeout(() => {
        manualReloadBanks();
      }, 100);
    } catch (error) {
      console.error('❌ Error saving template:', error);
      showError('Failed to save template. Please try again.');
      // Don't throw error, just return to keep modal open
      return;
    }
  };

  const handleOpenCopyDialog = (bank) => {
    setCopySourceBank(bank);
    setCopyDialogOpen(true);
  };

  const handleCloseCopyDialog = () => {
    setCopyDialogOpen(false);
    setCopySourceBank(null);
  };

  const handleCopyComplete = (result, candidateBanks) => {
    const successCount = result?.successes?.length || 0;
    const failures = result?.failures || [];
    const total = successCount + failures.length;

    if (failures.length === 0 && successCount > 0) {
      showSuccess(`Template copied to ${successCount} account${successCount === 1 ? '' : 's'}.`);
    } else if (successCount > 0) {
      const failedNames = failures
        .map((f) => candidateBanks?.find((b) => b.id === f.bankId)?.accountNumber || f.bankId)
        .join(', ');
      showWarning(
        `Template copied to ${successCount} of ${total} accounts. Failed for: ${failedNames}`
      );
    } else if (failures.length > 0) {
      const firstError = failures[0]?.message || 'Failed to copy template';
      showError(firstError);
    }

    setTimeout(() => {
      manualReloadBanks();
    }, 100);
  };

  const isTemplateConfigured = (bank) => {
    if (!bank) {
      return false;
    }
    // Use the HasChequeTemplate field directly from the API response
    return bank.hasChequeTemplate || false;
  };

  // Helper function to check if a bank has a template
  const hasTemplate = (bank) => {
    if (!bank) {
      return false;
    }
    return bank.hasChequeTemplate || false;
  };

  // Prevent body scrollbar to ensure only DataGrid scrolls
  useEffect(() => {
    // Hide all possible scrollbars when component mounts
    document.body.style.setProperty('overflow', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow', 'hidden', 'important');
    document.body.style.setProperty('overflow-x', 'hidden', 'important');
    document.body.style.setProperty('overflow-y', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-x', 'hidden', 'important');
    document.documentElement.style.setProperty('overflow-y', 'hidden', 'important');
    
    // Also target the main app container
    const appContainer = document.getElementById('root');
    if (appContainer) {
      appContainer.style.setProperty('overflow', 'hidden', 'important');
    }
    
    // Restore scrollbar when component unmounts
    return () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow-x');
      document.body.style.removeProperty('overflow-y');
      document.documentElement.style.removeProperty('overflow-x');
      document.documentElement.style.removeProperty('overflow-y');
      if (appContainer) {
        appContainer.style.removeProperty('overflow');
      }
    };
  }, []);

  return (
    <PageContainer>
      <HeaderSection>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }} color="primary">
            Bank Setup
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your bank accounts for cheque printing
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportOpen}
            sx={{
              borderRadius: 0,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Import Banks
          </Button>
          {backupEnabled && (
            <>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportConfig}
                disabled={exporting}
                sx={{
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                {exporting ? 'Exporting...' : 'Export Config'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RestoreConfigIcon />}
                onClick={() => setRestoreDialogOpen(true)}
                sx={{
                  borderRadius: 0,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Restore Config
              </Button>
            </>
          )}
          <EasyCheckButton
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddBank}
          >
            Add Bank Account
          </EasyCheckButton>
        </Stack>
      </HeaderSection>

      <Box sx={{ 
        flexGrow: 1, 
        minHeight: 0,
        maxHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}>
        <EasyCheckDataGridBasic
          rows={rows}
          columns={columns}
          loading={loading}
          checkboxSelection={false}
          disableRowSelectionOnClick
          disableColumnFilter={false}
          hideHorizontalScrollbar={true}
          autoWidth={true}
          scrollbarSize={0}
          rowHeight={64}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          slots={{
            toolbar: CustomToolbar,
            noRowsOverlay: () => (
              <Stack
                height="100%"
                alignItems="center"
                justifyContent="center"
                spacing={2}
                sx={{ py: 4 }}
              >
                <BankIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                <Typography variant="h6" color="text.secondary">
                  No bank accounts found
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Add your first bank account to get started
                </Typography>
              </Stack>
            ),
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
            preferencePanel: {
              open: false,
            },
          }}
          columnHeaderHeight={56}
          pageSizeOptions={[5, 10, 25, 50]}
          disableVirtualization
          pagination
          sx={{ 
            height: '100%',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            '& .MuiDataGrid-root': {
              border: 'none',
              borderRadius: 0,
            },
            '& .MuiDataGrid-main': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f5f5f5',
              borderRight: 'none !important',
              borderLeft: 'none !important',
              padding: '16px 12px',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#F7F9FC',
              borderBottom: '1px solid #e8e8e8 !important',
              borderTop: 'none !important',
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-columnHeader': {
              borderRight: 'none !important',
              borderLeft: 'none !important',
              borderBottom: 'none !important',
              color: '#000000 !important',
              display: 'flex !important',
              alignItems: 'center !important',
              justifyContent: 'flex-start !important',
              borderRadius: '0 !important',
              '&:first-of-type': {
                borderTopLeftRadius: '0 !important',
              },
              '&:last-of-type': {
                borderTopRightRadius: '0 !important',
              },
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#000000 !important',
              fontWeight: '600',
              lineHeight: '1 !important',
            },
            '& .MuiDataGrid-columnHeaderTitleContainer': {
              display: 'flex !important',
              alignItems: 'center !important',
              height: '100% !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f8f9fa',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid #f5f5f5',
            },
          }}
        />
      </Box>

      <BankFormModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleBankSave}
        bank={selectedBank}
      />

      <BankImportModal
        open={importModalOpen}
        onClose={handleImportClose}
        onImportComplete={handleImportComplete}
      />

      <TemplateFormModal
        open={templateModalOpen}
        onClose={handleTemplateModalClose}
        onSave={handleTemplateSave}
        bank={selectedBankForTemplate}
        template={currentTemplate}
      />

      <CopyTemplateDialog
        open={copyDialogOpen}
        onClose={handleCloseCopyDialog}
        sourceBank={copySourceBank}
        banks={banks}
        onComplete={handleCopyComplete}
        onError={(msg) => showError(msg)}
      />

      <BankConfigRestoreDialog
        open={restoreDialogOpen}
        banks={banks}
        onClose={() => setRestoreDialogOpen(false)}
        onImportComplete={() => {
          setTimeout(() => {
            manualReloadBanks();
          }, 100);
        }}
      />

      <TemplateHistoryDialog
        open={historyDialogOpen}
        onClose={() => {
          setHistoryDialogOpen(false);
          setHistoryBank(null);
        }}
        bank={historyBank}
        onRestored={() => {
          showSuccess('Template version restored');
          setTimeout(() => {
            manualReloadBanks();
          }, 100);
        }}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transition="slide"
        showIcon={true}
      />

      <Dialog
        open={detailModalOpen}
        onClose={handleDetailModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BankIcon color="primary" />
            <Typography variant="h6">
              Bank Account Details
            </Typography>
          </Box>
          <IconButton onClick={handleDetailModalClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBankDetail && (
            <Stack spacing={2}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Account Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {selectedBankDetail.accountNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Account Name
                    </Typography>
                    <Typography variant="body1">
                      {selectedBankDetail.accountName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Currency
                    </Typography>
                    <CurrencyChip
                      label={selectedBankDetail.currency}
                      size="small"
                      sx={{
                        backgroundColor: `${getCurrencyColor(selectedBankDetail.currency, theme)}15`,
                        color: getCurrencyColor(selectedBankDetail.currency, theme),
                        border: `1px solid ${getCurrencyColor(selectedBankDetail.currency, theme)}30`,
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Template Status
                    </Typography>
                    <Chip
                      icon={isTemplateConfigured(selectedBankDetail) ? <CheckIcon /> : <CancelIcon />}
                      label={isTemplateConfigured(selectedBankDetail) ? 'Configured' : 'Not Configured'}
                      size="small"
                      sx={{
                        backgroundColor: isTemplateConfigured(selectedBankDetail) 
                          ? theme.palette.success.light 
                          : theme.palette.error.light,
                        color: isTemplateConfigured(selectedBankDetail) 
                          ? theme.palette.success.dark 
                          : theme.palette.error.dark,
                        border: `1px solid ${isTemplateConfigured(selectedBankDetail) 
                          ? theme.palette.success.main 
                          : theme.palette.error.main}30`,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Cheque Template Configuration */}
              <Box>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  Cheque Template Configuration
                </Typography>
                
                {isTemplateConfigured(selectedBankDetail) ? (
                  <Stack spacing={2}>
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'success.light', 
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <CheckIcon sx={{ color: 'success.dark' }} />
                      <Typography variant="body2" sx={{ color: 'success.dark' }}>
                        Cheque template is configured for this bank
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Template configuration details are available in the template management section.
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CancelIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      No Template Configuration
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Cheque template coordinates have not been configured for this bank account.
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<TemplateIcon />}
                      onClick={() => {
                        handleDetailModalClose();
                        handleConfigureTemplate(selectedBankDetail);
                      }}
                      sx={{ mt: 2 }}
                    >
                      Configure Template
                    </Button>
                  </Box>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailModalClose}>
            Close
          </Button>
          <Button
            variant="outlined"
            startIcon={<TemplateIcon />}
            onClick={() => {
              handleDetailModalClose();
              handleConfigureTemplate(selectedBankDetail);
            }}
          >
            Configure Template
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              handleDetailModalClose();
              handleEditBank(selectedBankDetail);
            }}
          >
            Edit Bank
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default BankSetupPage; 